import { useCallback, useState } from 'react';
import { Box, Text, Button, Input, KeyboardSpacer } from '@betty/moonlight';
import {
  useNavigation,
  StackNavigationProp,
  StackActions,
  Screen,
} from '@betty/navigation';
import { AuthNavigatorParamsList } from '../navigators/AuthNavigator/AuthNavigator.types';
import { useDispatch } from '@betty/toolkit/state';
import { resetPasswordLink } from '../state';
import { z } from 'zod';

function ForgotPasswordScreen() {
  const [email, setEmail] = useState('');
  const [hasError, setHasError] = useState(false);

  const dispatch = useDispatch();
  const navigation =
    useNavigation<StackNavigationProp<AuthNavigatorParamsList>>();

  const emailSchema = z.string().email();

  const handleEmailChange = useCallback(
    (value: string) => {
      setEmail(value);

      const result = emailSchema.safeParse(value);
      if (!result.success) {
        setHasError(true);
      } else {
        setHasError(false);
      }
    },
    [emailSchema],
  );

  const onSubmitPress = useCallback(async () => {
    const result = await dispatch(resetPasswordLink(email));

    if (result.meta.requestStatus === 'fulfilled') {
      navigation.dispatch(
        StackActions.replace('ForgotPasswordSubmittedScreen'),
      );
    }
  }, [navigation, dispatch, email]);

  return (
    <Screen paddingBottom="safe-area-inset-bottom">
      <Box flex={1} justifyContent="space-between">
        <Box gap="space-600">
          <Text
            variant="h4"
            gradient="darkSurfaceAccent"
            locNS="auth"
            locKey="forgotPasswordTitle"
          />
          <Text
            variant="body-m"
            color="darkSurfacePrimary"
            locNS="auth"
            locKey="forgotPasswordDescription"
          />
          <Input
            placeholderKey="email"
            placeholderNS="auth"
            keyboardType="email-address"
            autoComplete="email"
            enterKeyHint="go"
            value={email}
            onChangeText={handleEmailChange}
            onSubmitEditing={onSubmitPress}
            error={hasError ? 'error' : undefined}
          />

          {hasError ? (
            <Text
              variant="caption"
              locNS="auth"
              locKey="enterValidEmail"
              color="darkSurfaceError"
            />
          ) : null}
        </Box>
        <Box>
          <Button
            size="large"
            locNS="auth"
            locKey="forgotPasswordButton"
            onPress={onSubmitPress}
            disabled={!email || hasError}
          />
          <KeyboardSpacer isInsideSafeAreaView additionalOffset={-30} />
        </Box>
      </Box>
    </Screen>
  );
}

export default ForgotPasswordScreen;
